import React, { useMemo, useState } from 'react'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import { showDocumentPreview } from '../../../../store/documents/actions'

import styles from './styles.module.scss'

import Table from '../../../Core/Table'

const columnHelper = createColumnHelper()

const getStatusColors = (status) => {
  const value = (status || '').toLowerCase()
  if (value.includes('valid') || value.includes('acknowledged') || value.includes('approved') || value.includes('signed')) {
    return { background: '#D1F9CB', text: '#32BF1F', border: '#BEF3B7' }
  }
  if (value.includes('pending') || value.includes('review')) {
    return { background: '#FFD4A5', text: '#D77000', border: '#E7C88C' }
  }
  if (value.includes('declined') || value.includes('overdue') || value.includes('expired') || value.includes('replacement')) {
    return { background: '#FFBDBD', text: '#D70000', border: '#F77C7C' }
  }
  return { background: '#E9E9E9', text: '#919191', border: '#C6C6C6' }
}

const formatDate = (date, withTime = false) => {
  if (!date || !moment(date).isValid()) return '-'
  return moment(date).format(withTime ? 'MMM D, HH:mm' : 'MMM D, YYYY')
}

const getEmployeeName = (employee = {}) => (
  employee.full_name || `${employee.name || ''} ${employee.surname || ''}`.trim() || '-'
)

const getUploadedFiles = (uploaded) => {
  if (!uploaded) return []
  const files = Array.isArray(uploaded.files)
    ? uploaded.files
    : [uploaded.file || uploaded]
  return files.map(file => {
    if (typeof file === 'string') return file.split('/').pop()
    return file?.original_name || file?.file_name || file?.name || file?.path?.split('/').pop()
  }).filter(Boolean)
}

const getReviewNote = (review) => (
  review?.comment || review?.note || review?.reason || review?.rejection_reason
)

const getSummary = (document, employees) => {
  return employees.reduce((acc, item) => {
    return {
      ...acc,
      assigned: acc.assigned + 1,
      approved: acc.approved + item.uploaded.review_status === 'approved' ? 1 : 0,
      rejected: acc.rejected + item.uploaded.review_status === 'rejected' ? 1 : 0,
      to_review: acc.to_review + (document.document_type.employee_upload_review && item.uploaded.value && (!item.uploaded.review_status ? 1 : 0)),
      not_uploaded: acc.not_uploaded + (!item.uploaded.value ? 1 : 0),
      overdue: acc.overdue + item.status.value === 'overdue' ? 1 : 0,
    }
  }, {
    assigned: 0,
    approved: 0,
    to_review: 0,
    not_uploaded: 0,
    rejected: 0,
    overdue: 0,
  })
}

const Employees = ({document, employees}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [selectedStatus, setSelectedStatus] = useState('assigned')

  const isCompanyOwnership = document.document_type.ownership === 'company'

  const statuses = [
    {key: 'assigned', title: t('Assigned')},
    {key: 'approved', title: t('Approved')},
    {key: 'to_review', title: t('To review')},
    {key: 'not_uploaded', title: t('Not uploaded')},
    {key: 'rejected', title: t('Rejected')},
    {key: 'overdue', title: t('Overdue')},
  ]

  const summary = getSummary(document, employees)

  const filteredEmployees = employees.filter(employee => {
    switch (selectedStatus) {
      case 'approved':
        return employee.uploaded.review_status === 'approved'
      case 'rejected':
        return employee.uploaded.review_status === 'rejected'
      case 'to_review':
        return document.document_type.employee_upload_review && !employee.uploaded.review_status
      case 'not_uploaded':
        return !employee.uploaded.value
      case 'overdue':
        return employee.status.value === 'overdue'
      default:
        return true
    }
  })

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'employee',
      header: t('Employee'),
      enableSorting: false,
      cell: ({ row }) => {
        const employee = row.original.employee || {}
        const initials = `${(employee.name || '').charAt(0)}${(employee.surname || '').charAt(0)}`.toUpperCase()
        return (
          <div className={styles.employeeCell}>
            {
              employee.avatar
                ? <img className={styles.avatar} src={`${window.location.origin}/storage/${employee.avatar}`} alt='' />
                : <div className={styles.avatar}>{initials}</div>
            }
            <div className={styles.employeeInfo}>
              <div className={styles.employeeName}>{getEmployeeName(employee)}</div>
              {
                employee.place?.name
                  ? <div className={styles.employeePlace}>{employee.place.name}</div>
                  : null
              }
            </div>
          </div>
        )
      },
    }),
    columnHelper.display({
      id: 'status',
      header: t('Status'),
      enableSorting: false,
      cell: ({ row }) => {
        const status = row.original.status || {}
        const colors = getStatusColors(status.value)
        const reviewNote = getReviewNote(row.original.review)
        return (
          <div className={styles.statusCell}>
            <div className={styles.statusBadge}
              style={{
                backgroundColor: colors.background,
                color: colors.text,
                border: `1px solid ${colors.border}`,
              }}>
              <span className={styles.statusDot} />
              {status.label || t(status.value || 'Unknown')}
            </div>
            {reviewNote ? <div className={styles.reviewNote}>{reviewNote}</div> : null}
          </div>
        )
      },
    }),
    ...(
      isCompanyOwnership
        ? [
            columnHelper.display({
              id: 'seen',
              header: t('Seen'),
              enableSorting: false,
              cell: ({ row }) => {
                const seen = row.original.seen
                const seenAt = seen.last_seen_at || seen.first_viewed_at
                return <span className={styles.mutedText}>{formatDate(seenAt, true)}</span>
              },
            }),
            columnHelper.display({
              id: 'acknowledged',
              header: t('Acknowledged'),
              enableSorting: false,
              cell: ({ row }) => {
                const acknowledged = row.original.acknowledged
                const acknowledgedAt = acknowledged.at
                return <span className={styles.mutedText}>{formatDate(acknowledgedAt, true)}</span>
              },
            }),
            columnHelper.display({
              id: 'signed',
              header: t('Signed'),
              enableSorting: false,
              cell: ({ row }) => {
                const signed = row.original.signed
                const signedAt = signed.at
                return <span className={styles.mutedText}>{formatDate(signedAt, true)}</span>
              },
            }),
          ]
        : [
          columnHelper.display({
            id: 'uploaded',
            header: t('Uploaded'),
            enableSorting: false,
            cell: ({ row }) => {
              const uploaded = row.original.uploaded
              const uploadedAt = uploaded?.at || uploaded?.created_at || uploaded?.date
              return <span className={styles.mutedText}>{formatDate(uploadedAt, true)}</span>
            },
          }),
          columnHelper.display({
            id: 'file',
            header: t('File'),
            enableSorting: false,
            cell: ({ row }) => {
              const files = getUploadedFiles(row.original.uploaded)
              return files.length
                ? <span className={styles.fileName} title={files.join(', ')}>{files.join(', ')}</span>
                : <span className={styles.mutedText}>-</span>
            },
          }),
          columnHelper.display({
            id: 'reviewed',
            header: t('Reviewed'),
            enableSorting: false,
            cell: ({ row }) => {
              const review = row.original.uploaded
              const reviewedAt = review?.reviewed_at || review?.updated_at || review?.created_at
                || row.original.approved_at || row.original.signed_at
              return <span className={styles.mutedText}>{formatDate(reviewedAt, true)}</span>
            },
          }),
        ]
    ),
    columnHelper.display({
      id: 'due_date',
      header: t('Due date'),
      enableSorting: false,
      cell: ({ row }) => (
        <span className={styles.mutedText}>
          {formatDate(row.original.due_date_for_action)}
        </span>
      ),
    }),
  ], [t, isCompanyOwnership])

  const table = useReactTable({
    data: filteredEmployees,
    columns,
    getRowId: row => String(row.id || row.employee?.id),
    getCoreRowModel: getCoreRowModel(),
  })

  const handleClickDocument = (row) => {
    dispatch(showDocumentPreview({documentId: document.id, employeeId: row.employee.id}))
  }

  return (
    <>
      <div className={styles.topFilters} role='tablist' aria-label={t('Document status')}>
        {
          statuses.map((filter) => (
            <button
              key={filter.key}
              type='button'
              role='tab'
              aria-selected={selectedStatus === filter.key}
              className={`${styles.topFilter} ${selectedStatus === filter.key ? styles.topFilterActive : ''}`}
              onClick={() => setSelectedStatus(filter.key)}
            >
              <span className={styles.topFilterTitle}>{filter.title}</span>
              <span className={styles.topFilterCount}>{summary[filter.key] || 0}</span>
            </button>
          ))
        }
      </div>
      <Table
        table={table}
        columns={columns}
        placeholder={t('No employees found')}
        headerStyles={{ padding: '6px 10px' }}
        onClickRow={handleClickDocument} />
    </>
  )
}

export default Employees
